import { ExportMenu } from "@/components/ExportMenu";
import { useEffect, useMemo, useState } from "react";
import api, { money, fmtDateOnly } from "@/lib/api";
import Pagination from "@/components/Pagination";
import { useActivity } from "@/context/ActivityContext";
import { ActivityPicker } from "@/components/ActivityPicker";
import { ComposedChart, Line, Legend, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, ShoppingBag, HandCoins, Wallet, BarChart3, Boxes, Package, Search, Sparkles } from "lucide-react";

const COLORS = ["#2563EB", "#16A34A", "#F59E0B", "#0891B2", "#7C3AED", "#DC2626"];
const TOP_MODES = [["revenue", "Chiffre d'affaires"], ["qty", "Quantité vendue"], ["margin", "Marge brute"]];

// Agrégation hebdomadaire des séries longues (> 45 jours) pour éviter qu'une journée isolée écrase le graphique.
const aggregateSeries = (byDay) => {
  if (!byDay || byDay.length <= 45) return { data: byDay || [], unit: "jour" };
  const weeks = new Map();
  byDay.forEach((p) => {
    const dt = new Date(p.date + "T00:00:00Z");
    const monday = new Date(dt); monday.setUTCDate(dt.getUTCDate() - ((dt.getUTCDay() + 6) % 7));
    const k = monday.toISOString().slice(0, 10);
    const w = weeks.get(k) || { date: k, revenue: 0, margin: 0 };
    w.revenue += p.revenue; w.margin += p.margin; weeks.set(k, w);
  });
  return { data: [...weeks.values()], unit: "semaine" };
};

const marginTone = (pct) =>
  pct < 0 ? { badge: "bg-[#FEE2E2] text-[#B91C1C]", label: "Perte" }
    : pct < 10 ? { badge: "bg-[#FEF3C7] text-[#B45309]", label: "Faible" }
      : pct < 30 ? { badge: "bg-[#EFF6FF] text-[#2563EB]", label: "Normal" }
        : { badge: "bg-[#DCFCE7] text-[#15803D]", label: "Très rentable" };

const KPI_TONES = {
  navy: { bg: "bg-[#16324F]/[0.06]", border: "border-[#16324F]/15", text: "#16324F" },
  blue: { bg: "bg-[#EFF6FF]", border: "border-[#DBEAFE]", text: "#1D4ED8" },
  green: { bg: "bg-[#ECFDF3]", border: "border-[#BBF7D0]", text: "#15803D" },
  red: { bg: "bg-[#FEF2F2]", border: "border-[#FECACA]", text: "#B91C1C" },
  amber: { bg: "bg-[#FFFBEB]", border: "border-[#FDE68A]", text: "#B45309" },
  violet: { bg: "bg-[#EEF2FF]", border: "border-[#C7D2FE]", text: "#4338CA" },
};

const KpiCard = ({ icon: Icon, label, value, tone = "blue", hint, trend, prev, periods }) => {
  const t = KPI_TONES[tone] || KPI_TONES.blue;
  const big = trend != null && Math.abs(trend) >= 300;
  return (
    <div className={`rounded-2xl border p-4 ${t.bg} ${t.border} transition-transform duration-200 hover:-translate-y-0.5`} data-testid={`kpi-${label}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="kpi-label leading-tight" style={{ color: t.text }}>{label}</span>
        <span className="grid place-items-center w-9 h-9 rounded-xl bg-white shadow-sm shrink-0"><Icon className="w-5 h-5" style={{ color: t.text }} /></span>
      </div>
      <div className="mt-2 text-lg md:text-2xl font-extrabold font-head text-[#0F172A] tracking-tight break-words tabular-nums">{value}</div>
      {periods && <div className="text-[10px] text-[#64748B] mt-0.5">du {fmtDateOnly(periods.current.from)} au {fmtDateOnly(periods.current.to)}</div>}
      {trend != null && (
        <div className="mt-1.5" data-testid={`kpi-trend-${label}`}>
          <span className={`inline-flex items-center gap-1 text-xs font-bold px-1.5 py-0.5 rounded-md ${trend >= 0 ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FEE2E2] text-[#B91C1C]"}`}>
            {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{big ? `× ${((100 + trend) / 100).toLocaleString("fr-FR", { maximumFractionDigits: 1 })}` : `${trend > 0 ? "+" : ""}${trend} %`}
          </span>
          {prev != null && <div className="text-[10px] text-[#64748B] mt-0.5">contre {prev} du {fmtDateOnly(periods?.previous?.from)} au {fmtDateOnly(periods?.previous?.to)}</div>}
        </div>
      )}
      {trend == null && prev != null && <div className="text-[10px] text-[#64748B] mt-1.5">Aucune vente du {fmtDateOnly(periods?.previous?.from)} au {fmtDateOnly(periods?.previous?.to)} — pas de comparaison</div>}
      {hint && <div className="text-[10px] text-[#64748B] mt-1 leading-tight">{hint}</div>}
    </div>
  );
};

export default function Reports() {
  const { current, settings } = useActivity();
  const [days, setDays] = useState(30);
  const [periodMode, setPeriodMode] = useState("preset");
  const [customFrom, setCustomFrom] = useState(new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10));
  const [customTo, setCustomTo] = useState(new Date().toISOString().slice(0, 10));
  const [locId, setLocId] = useState("");
  const [sellerId, setSellerId] = useState("");
  const [locs, setLocs] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [d, setD] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("margin");
  const [showNoSales, setShowNoSales] = useState(false);
  const [topMode, setTopMode] = useState("revenue");

  useEffect(() => {
    if (!current?.id) return;
    const q = `?activity_id=${current.id}`;
    setLocId("");
    setSellerId("");
    api.get(`/locations${q}`).then((r) => setLocs(r.data || [])).catch(() => {});
    api.get(`/users${q}`).then((r) => setSellers(r.data || [])).catch(() => {});
  }, [current?.id]);

  const customActive = periodMode === "custom" && customFrom && customTo && customFrom <= customTo;

  const isGlobal = !current || current.global;

  useEffect(() => {
    // Changement d'activité : anciens chiffres effacés pendant le chargement. Vue globale : aucun chiffre.
    setD(null);
    if (isGlobal) return;
    let q = `activity_id=${current.id}`;
    if (customActive) q += `&from_date=${customFrom}&to_date=${customTo}`;
    else q += `&days=${days}`;
    if (locId) q += `&location_id=${locId}`;
    if (sellerId) q += `&seller_id=${sellerId}`;
    setPage(1);
    api.get(`/reports/summary?${q}`).then((r) => setD(r.data)).catch(() => {});
  }, [current, days, periodMode, customFrom, customTo, locId, sellerId]); // eslint-disable-line

  const filteredMargins = useMemo(() => {
    if (!d?.product_margins) return [];
    let list = d.product_margins.filter((p) => p.name.toLowerCase().includes(search.toLowerCase().trim()));
    if (!showNoSales) list = list.filter((p) => (p.base_qty || 0) > 0 || (p.revenue || 0) > 0);
    const key = sortBy === "revenue" ? "revenue" : sortBy === "qty" ? "base_qty" : "margin";
    return [...list].sort((a, b) => b[key] - a[key]);
  }, [d, search, sortBy, showNoSales]);

  if (isGlobal) return <ActivityPicker title="Rapports & Stats" />;
  if (!d) return <div className="text-[#64748B] animate-pulse">Chargement des statistiques…</div>;

  const marginRate = d.total_revenue ? Math.round((d.margin / d.total_revenue) * 100) : 0;
  const growth = d.revenue_growth_pct;

  const periods = { current: d.period, previous: d.prev_period };
  const kpis = [
    { label: "Chiffre d'affaires", value: money(d.total_revenue), icon: TrendingUp, tone: "navy", trend: growth, prev: money(d.prev_revenue), periods },
    { label: "Ventes", value: d.sales_count, icon: ShoppingBag, tone: "blue", trend: d.sales_count_growth_pct, prev: String(d.prev_sales_count ?? 0), periods },
    { label: "Panier moyen", value: money(d.avg_basket), icon: Wallet, tone: "violet", trend: d.avg_basket_growth_pct, prev: money(d.prev_avg_basket), periods },
    { label: "Marge brute", value: money(d.margin), icon: BarChart3, tone: "green", trend: d.margin_growth_pct, prev: money(d.prev_margin), periods, hint: d.margin_untracked ? `dont suivie ${money(d.margin_tracked)} · non suivie ${money(d.margin_untracked)}` : `taux ${marginRate}%` },
    { label: "Encaissé", value: money(d.total_paid), icon: HandCoins, tone: "green" },
    { label: "Crédit en cours", value: money(d.total_credit), icon: HandCoins, tone: "red" },
    { label: "Valeur du stock", value: money(d.stock_value), icon: Boxes, tone: "amber", hint: "au coût d'achat" },
  ];
  const series = aggregateSeries(d.by_day);
  const topList = (d.top_products_by || {})[topMode] || d.top_products || [];
  const topVal = (p) => topMode === "qty" ? p.base_qty : topMode === "margin" ? p.margin : p.revenue;
  const topFmt = (p) => topMode === "qty" ? `${new Intl.NumberFormat("fr-FR").format(p.base_qty)} ${p.base_unit || ""}`.trim() : money(topVal(p));

  const glassSelect = "rounded-xl bg-white/15 border border-white/25 text-white text-sm px-3 py-2 outline-none backdrop-blur focus:bg-white/25 [&>option]:text-[#0F172A]";
  return (
    <div className="space-y-5 md:space-y-6">
      {/* Hero — titre, filtres, actions + insight décisionnel */}
      <div className="relative overflow-hidden rounded-2xl p-4 sm:p-6 text-white shadow-lg animate-fade-up" data-testid="executive-decision-hero"
        style={{ background: "linear-gradient(160deg,#1A3A5C 0%,#16324F 55%,#0E2135 100%)" }}>
        <div className="pointer-events-none absolute -right-16 -top-16 w-56 h-56 rounded-full bg-white/5 blur-2xl" />
        <div className="pointer-events-none absolute -left-10 bottom-0 w-40 h-40 rounded-full bg-white/5 blur-2xl" />
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 sm:gap-4">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-white/70"><Sparkles className="w-3.5 h-3.5" /> Analyse décisionnelle</div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold font-head mt-1">Rapports &amp; Statistiques</h1>
              <p className="text-xs sm:text-sm text-white/75 mt-0.5">{current ? current.name : "Toutes activités"} · performance &amp; décisions</p>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              {current?.stock_mode !== "simple" && (
              <select className={glassSelect} value={locId} onChange={(e) => setLocId(e.target.value)} data-testid="reports-location-filter">
                <option value="">Tous les points de vente</option>
                {locs.map((l) => <option key={l.id} value={l.id}>{l.name}{l.type === "warehouse" ? " (entrepôt)" : ""}</option>)}
              </select>
              )}
              <select className={glassSelect} value={sellerId} onChange={(e) => setSellerId(e.target.value)} data-testid="reports-seller-filter">
                <option value="">Tous les caissiers</option>
                {sellers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <select className={glassSelect} value={periodMode === "custom" ? "custom" : String(days)} onChange={(e) => { if (e.target.value === "custom") { setPeriodMode("custom"); } else { setPeriodMode("preset"); setDays(Number(e.target.value)); } }} data-testid="reports-period-select">
                <option value={1}>Aujourd'hui</option>
                <option value={7}>7 derniers jours</option>
                <option value={30}>30 derniers jours</option>
                <option value={new Date().getDate()}>Ce mois-ci</option>
                <option value={Math.max(1, Math.ceil((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000))}>Cette année</option>
                <option value="custom">Période personnalisée…</option>
              </select>
              {periodMode === "custom" && (
                <div className="flex items-center gap-1.5" data-testid="reports-custom-range">
                  <input type="date" className={glassSelect} value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} data-testid="reports-from-date" />
                  <span className="text-white/70 text-sm">→</span>
                  <input type="date" className={glassSelect} value={customTo} min={customFrom} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setCustomTo(e.target.value)} data-testid="reports-to-date" />
                </div>
              )}
              <ExportMenu dataset="sales" params={{ activity_id: current?.id, location_id: locId, seller_id: sellerId }} />
            </div>
          </div>
        </div>
      </div>

      {/* Zone 4 — KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 animate-fade-up" style={{ animationDelay: "120ms" }} data-testid="kpis-summary-grid">
        {kpis.map((k) => <KpiCard key={k.label} {...k} />)}
      </div>

      {/* Zone 5 — Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6 animate-fade-up" style={{ animationDelay: "180ms" }}>
        <div className="card p-5 lg:col-span-8" data-testid="revenue-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold font-head text-[#16324F]">Chiffre d'affaires &amp; marge brute</h3>
              <p className="text-xs text-[#64748B] mt-0.5">Barres = CA · Ligne = marge brute, par {series.unit}{series.unit === "semaine" ? " (période longue : agrégation hebdomadaire)" : ""}</p>
            </div>
            {growth != null && (
              <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg ${growth >= 0 ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FEE2E2] text-[#B91C1C]"}`}>
                {growth >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}{growth > 0 ? "+" : ""}{growth}% vs {d.prev_period ? `${fmtDateOnly(d.prev_period.from)} → ${fmtDateOnly(d.prev_period.to)}` : "période préc."}
              </span>
            )}
          </div>
          {d.total_revenue === 0 && d.sales_count === 0 ? <div className="text-center text-[#94A3B8] py-16 text-sm">Aucune vente sur la période. Effectuez des ventes dans la Caisse (POS).</div> : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={series.data} margin={{ left: -8, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="barRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.95} />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0.55} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => v.slice(5)} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={18} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={54} tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
                <Tooltip formatter={(v, name) => [money(v), name]} labelFormatter={(l) => series.unit === "semaine" ? `Semaine du ${l}` : `Le ${l}`} contentStyle={{ borderRadius: 12, border: "1px solid #E7ECF3", fontSize: 13 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
                <Bar dataKey="revenue" name="Chiffre d'affaires" fill="url(#barRevenue)" radius={[6, 6, 0, 0]} maxBarSize={42} />
                <Line type="monotone" dataKey="margin" name="Marge brute" stroke="#16A34A" strokeWidth={2.5} dot={series.data.length <= 31 ? { r: 2.5, fill: "#16A34A" } : false} activeDot={{ r: 4 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card p-5 lg:col-span-4" data-testid="category-chart-card">
          <h3 className="text-lg font-bold font-head text-[#16324F] mb-2">Répartition par catégorie</h3>
          {d.by_category.length === 0 ? <div className="text-center text-[#94A3B8] py-16 text-sm">Aucune donnée</div> : (
            <>
              <div className="relative">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={d.by_category} dataKey="revenue" nameKey="name" cx="50%" cy="50%" innerRadius={54} outerRadius={80} paddingAngle={2} stroke="none">
                      {d.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E7ECF3", fontSize: 13 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-bold">CA total</div>
                  <div className="text-base font-extrabold font-head text-[#16324F]">{money(d.total_revenue)}</div>
                </div>
              </div>
              <div className="space-y-1.5 mt-3">
                {d.by_category.slice(0, 5).map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-xs" data-testid={`category-legend-${i}`}>
                    <span className="flex items-center gap-2 text-[#334155] truncate"><span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: COLORS[i % COLORS.length] }} />{c.name}</span>
                    <span className="font-semibold text-[#16324F]">{d.total_revenue ? Math.round((c.revenue / d.total_revenue) * 100) : 0}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Décisionnel : points de vente, remises, créances, bénéfice net, modes de paiement */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card p-5" data-testid="net-profit-card">
          <h3 className="text-lg font-bold font-head text-[#16324F]">Bénéfice net de la période</h3>
          <p className="text-xs text-[#64748B] mb-3">du {fmtDateOnly(d.period?.from)} au {fmtDateOnly(d.period?.to)}</p>
          <div className={`text-3xl font-extrabold font-head tabular-nums ${d.net_profit >= 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`} data-testid="net-profit-value">{money(d.net_profit)}</div>
          <div className="mt-3 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-[#64748B]">Marge brute</span><span className="font-semibold text-[#15803D]">{money(d.margin)}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">− Dépenses enregistrées</span><span className="font-semibold text-[#B91C1C]">−{money(d.expenses)}</span></div>
            {d.hr_cost > 0 && <div className="flex justify-between"><span className="text-[#64748B]">− Salaires (RH)</span><span className="font-semibold text-[#B91C1C]">−{money(d.hr_cost)}</span></div>}
            {d.fleet_cost > 0 && <div className="flex justify-between"><span className="text-[#64748B]">− Logistique</span><span className="font-semibold text-[#B91C1C]">−{money(d.fleet_cost)}</span></div>}
          </div>
        </div>

        <div className="card p-5" data-testid="discounts-card">
          <h3 className="text-lg font-bold font-head text-[#16324F]">Remises de la période</h3>
          <p className="text-xs text-[#64748B] mb-3">Remises catalogue, exceptionnelles et prix négociés accordés sur les ventes</p>
          <div className="flex items-end gap-3">
            <div className="text-3xl font-extrabold font-head tabular-nums text-[#B45309]" data-testid="discounts-total">{d.discounts?.total > 0 ? `−${money(d.discounts.total)}` : money(0)}</div>
            <div className={`mb-1 text-xs font-bold px-2 py-0.5 rounded-md ${d.discounts_pct >= 5 ? "bg-[#FEE2E2] text-[#B91C1C]" : d.discounts_pct >= 2 ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#DCFCE7] text-[#15803D]"}`}>{d.discounts_pct} % du CA brut</div>
          </div>
          <div className="mt-3 space-y-1.5 text-sm">
            {[["catalog", "Remise catalogue (prix par conditionnement, promotions)"], ["exceptional", "Remise exceptionnelle / prix manuel"], ["negotiated", "Prix net négocié"]].map(([k, l]) => (
              <div key={k} className="flex justify-between gap-3" data-testid={`discounts-${k}`}><span className="text-[#64748B]">{l}</span><span className="font-semibold text-[#0F172A] whitespace-nowrap">{money(d.discounts?.[k] || 0)}</span></div>
            ))}
          </div>
        </div>

        <div className="card p-5" data-testid="receivables-aging-card">
          <h3 className="text-lg font-bold font-head text-[#16324F]">Vieillissement des créances</h3>
          <p className="text-xs text-[#64748B] mb-3">Créances encore ouvertes aujourd'hui · total <b className="text-[#B45309]">{money(d.receivables_open_total)}</b>{d.recovery_rate_pct != null && <> · taux de recouvrement de la période <b className={d.recovery_rate_pct >= 90 ? "text-[#15803D]" : d.recovery_rate_pct >= 70 ? "text-[#B45309]" : "text-[#B91C1C]"}>{d.recovery_rate_pct} %</b></>}</p>
          <div className="grid grid-cols-3 gap-2">
            {[["b0_7", "0 – 7 jours", "bg-[#ECFDF3] border-[#BBF7D0] text-[#15803D]"], ["b8_30", "8 – 30 jours", "bg-[#FFFBEB] border-[#FDE68A] text-[#B45309]"], ["b31", "+ 30 jours", "bg-[#FEF2F2] border-[#FECACA] text-[#B91C1C]"]].map(([k, l, cls]) => {
              const b = d.receivables_aging?.[k] || { amount: 0, count: 0 };
              const pct = d.receivables_open_total ? Math.round((b.amount / d.receivables_open_total) * 100) : 0;
              return (
                <div key={k} className={`rounded-xl border px-3 py-2.5 min-w-0 ${cls}`} data-testid={`aging-${k}`}>
                  <div className="text-[11px] font-bold uppercase tracking-wide">{l}</div>
                  <div className="text-lg font-extrabold text-[#0F172A] break-words tabular-nums">{money(b.amount)}</div>
                  <div className="text-[11px] opacity-80">{b.count} vente(s) · {pct} %</div>
                </div>
              );
            })}
          </div>
          {d.receivables_aging?.b31?.amount > 0 && <p className="text-xs text-[#B91C1C] mt-2 font-medium">{money(d.receivables_aging.b31.amount)} dus depuis plus de 30 jours : à relancer en priorité.</p>}
        </div>

        <div className="card p-5" data-testid="payment-mix-card">
          <h3 className="text-lg font-bold font-head text-[#16324F]">Répartition par mode de paiement</h3>
          <p className="text-xs text-[#64748B] mb-3">Ventes de la période selon le mode choisi à la caisse</p>
          {(() => {
            const LBL = { cash: "Espèces", mobile_money: "Mobile Money", partial: "Paiement partiel", card: "Carte", store_credit: "Avoir client", other: "Autre" };
            const list = (d.by_payment || []).filter((p) => !["credit", "bank"].includes(p.type));
            const tot = list.reduce((a, p) => a + p.amount, 0);
            if (!list.length) return <div className="text-center text-[#94A3B8] py-8 text-sm">Aucune vente sur la période</div>;
            return (
              <div className="space-y-2.5">
                {list.map((p, i) => (
                  <div key={p.type} data-testid={`payment-mix-${p.type}`}>
                    <div className="flex justify-between text-sm mb-1"><span className="font-medium text-[#0F172A]">{LBL[p.type] || p.type}</span><span className="text-[#16324F] font-bold">{money(p.amount)} <span className="text-xs font-semibold text-[#64748B]">· {p.count} vente(s) · {tot ? Math.round((p.amount / tot) * 100) : 0} %</span></span></div>
                    <div className="h-2.5 rounded-full bg-[#EEF2F7] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${tot ? (p.amount / tot) * 100 : 0}%`, background: COLORS[i % COLORS.length] }} /></div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {(d.by_location || []).length > 1 && (
        <div className="card p-5" data-testid="by-location-card">
          <h3 className="text-lg font-bold font-head text-[#16324F]">Comparaison par point de vente</h3>
          <p className="text-xs text-[#64748B] mb-3">du {fmtDateOnly(d.period?.from)} au {fmtDateOnly(d.period?.to)}</p>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm"><thead><tr><th className="th">Point de vente</th><th className="th text-right">CA</th><th className="th text-right">Marge brute</th><th className="th text-right">Ventes</th><th className="th text-right">Panier moyen</th><th className="th text-right">Part du CA</th></tr></thead>
              <tbody>{d.by_location.map((l) => (
                <tr key={l.location_id} className="row" data-testid={`loc-row-${l.location_id}`}><td className="td font-medium">{l.name}</td><td className="td text-right font-bold text-[#1D4ED8]">{money(l.revenue)}</td><td className="td text-right font-semibold text-[#15803D]">{money(l.margin)}</td><td className="td text-right">{l.count}</td><td className="td text-right">{money(l.avg_basket)}</td><td className="td text-right">{d.total_revenue ? Math.round((l.revenue / d.total_revenue) * 100) : 0} %</td></tr>
              ))}</tbody></table>
          </div>
          <div className="md:hidden divide-y divide-[#E2E8F0]">
            {d.by_location.map((l) => (
              <div key={l.location_id} className="py-3 first:pt-0" data-testid={`loc-card-${l.location_id}`}>
                <div className="flex justify-between gap-3"><span className="font-semibold text-[#0F172A]">{l.name}</span><span className="font-bold text-[#1D4ED8] whitespace-nowrap">{money(l.revenue)}</span></div>
                <div className="text-xs text-[#64748B] mt-1">Marge {money(l.margin)} · {l.count} vente(s) · panier {money(l.avg_basket)} · {d.total_revenue ? Math.round((l.revenue / d.total_revenue) * 100) : 0} % du CA</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top produits */}
      <div className="card p-5 animate-fade-up" style={{ animationDelay: "220ms" }} data-testid="top-products-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-lg font-bold font-head text-[#16324F]" data-testid="top-products-title">
            Top produits par {TOP_MODES.find(([k]) => k === topMode)[1].toLowerCase()}
            <span className="block text-xs font-medium text-[#64748B] mt-0.5">
              {d.period ? `du ${fmtDateOnly(d.period.from)} au ${fmtDateOnly(d.period.to)}` : "sur la période sélectionnée"} · 5 produits maximum
            </span>
          </h3>
          <div className="inline-flex rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-1 gap-1" role="tablist" data-testid="top-products-mode">
            {TOP_MODES.map(([k, l]) => (
              <button key={k} onClick={() => setTopMode(k)} data-testid={`top-mode-${k}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${topMode === k ? "bg-[#16324F] text-white shadow-sm" : "text-[#475569] hover:bg-white"}`}>{l}</button>
            ))}
          </div>
        </div>
        {topMode === "qty" && <p className="text-xs text-[#64748B] -mt-2 mb-3">Quantités ramenées à l'unité de base (ex. 2 packs de 12 = 24 bouteilles).</p>}
        {topList.length === 0 ? <div className="text-center text-[#94A3B8] py-10 text-sm">Aucune donnée</div> : (
          <div className="space-y-3.5">
            {topList.map((p, i) => {
              const max = topVal(topList[0]) || 1;
              return (
                <div key={i} data-testid={`top-product-${i}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-semibold text-[#0F172A] flex items-center gap-2"><span className="w-5 h-5 rounded-md text-[11px] font-bold flex items-center justify-center text-white" style={{ background: COLORS[i % COLORS.length] }}>{i + 1}</span>{p.name}</span>
                    <span className="text-[#2563EB] font-bold" data-testid={`top-product-value-${i}`}>{topFmt(p)}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-[#EEF2F7] overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(0, (topVal(p) / max) * 100)}%`, background: COLORS[i % COLORS.length] }} /></div>
                  <div className="text-xs text-[#94A3B8] mt-1">{new Intl.NumberFormat("fr-FR").format(p.base_qty ?? p.qty)} {p.base_unit || ""} vendu(s) · CA {money(p.revenue)} · marge brute {money(p.margin)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Zone 6 — Matrice marge par produit */}
      <div className="card p-5 animate-fade-up" style={{ animationDelay: "260ms" }} data-testid="product-margins-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2"><Package className="w-4 h-4 text-[#2563EB]" /><h3 className="text-lg font-bold font-head text-[#16324F]">Marge brute par produit</h3></div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <div className="relative">
              <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
              <input className="inp !py-2 !pl-9 !w-56" placeholder="Rechercher un produit…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="product-margin-search" />
            </div>
            <select className="inp !w-auto !py-2" value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }} data-testid="product-margin-sort">
              <option value="margin">Trier par marge</option>
              <option value="revenue">Trier par CA</option>
              <option value="qty">Trier par quantité</option>
            </select>
            <label className="inline-flex items-center gap-2 text-sm text-[#475569] cursor-pointer select-none whitespace-nowrap" data-testid="show-no-sales-toggle">
              <input type="checkbox" className="w-4 h-4 rounded border-[#CBD5E1] accent-[#2563EB]" checked={showNoSales} onChange={(e) => { setShowNoSales(e.target.checked); setPage(1); }} />
              Afficher les produits sans vente
            </label>
          </div>
        </div>
        {filteredMargins.length === 0 ? <div className="text-center text-[#94A3B8] py-10 text-sm">{search ? "Aucun produit ne correspond à votre recherche." : "Aucune vente sur la période."}</div> : (
          <>
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm min-w-[680px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase text-[#64748B] bg-[#F5F8FC]">
                    <th className="py-2.5 px-3 font-bold rounded-l-lg">Produit</th>
                    <th className="py-2.5 px-3 font-bold text-right">Qté vendue</th>
                    <th className="py-2.5 px-3 font-bold text-right">CA</th>
                    <th className="py-2.5 px-3 font-bold text-right">Coût d'achat</th>
                    <th className="py-2.5 px-3 font-bold text-right">Marge brute</th>
                    <th className="py-2.5 px-3 font-bold text-right rounded-r-lg">Marge %</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMargins.slice((page - 1) * 10, page * 10).map((p, i) => {
                    const tone = marginTone(p.margin_pct);
                    return (
                      <tr key={i} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`product-margin-row-${i}`}>
                        <td className="py-2.5 px-3 font-semibold text-[#0F172A]">{p.name}</td>
                        <td className="py-2.5 px-3 text-right text-[#334155]">{p.base_qty}{p.base_unit ? ` ${p.base_unit}` : ""}</td>
                        <td className="py-2.5 px-3 text-right text-[#334155]">{money(p.revenue)}</td>
                        <td className="py-2.5 px-3 text-right text-[#94A3B8]">{money(p.cogs)}</td>
                        <td className={`py-2.5 px-3 text-right font-bold ${p.margin < 0 ? "text-[#B91C1C]" : "text-[#15803D]"}`}>{money(p.margin)}</td>
                        <td className="py-2.5 px-3 text-right"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${tone.badge}`} title={tone.label}>{p.margin_pct}%</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-3"><Pagination page={page} setPage={setPage} total={filteredMargins.length} /></div>
          </>
        )}
      </div>
    </div>
  );
}
